<template>
    <div class="deal-notes">
        <div v-if="loading" class="notes-loading">
            <div class="loading-skeleton">
                <div class="skeleton-item">
                    <div class="skeleton-header">
                        <div class="skeleton-title"></div>
                        <div class="skeleton-meta">
                            <div class="skeleton-date"></div>
                            <div class="skeleton-owner"></div>
                        </div>
                    </div>
                    <div class="skeleton-content"></div>
                </div>
                <div class="skeleton-item">
                    <div class="skeleton-header">
                        <div class="skeleton-title"></div>
                        <div class="skeleton-meta">
                            <div class="skeleton-date"></div>
                            <div class="skeleton-owner"></div>
                        </div>
                    </div>
                    <div class="skeleton-content"></div>
                </div>
            </div>
        </div>

        <div v-else-if="notes.length === 0" class="no-notes">
            <i class="material-icons">note</i>
            <span>{{ $t("message.noNotesAdded") }}</span>
        </div>

        <div v-else class="notes-list">
            <div v-for="note in notes" :key="note.id" class="note-item">
                <div class="note-header">
                    <div class="note-title">
                        <i class="material-icons">description</i>
                        {{ note.Note_Title }}
                    </div>
                    <div class="note-meta">
                        <span class="note-owner">
                            <i class="material-icons">person</i>
                            {{ $_.get(note, "Owner.name", "") }}
                        </span>
                        <span class="note-date">
                            <i class="material-icons">schedule</i>
                            {{ formatDate(note.Created_Time) }}
                        </span>
                    </div>
                </div>

                <div class="note-content">{{ note.Note_Content }}</div>

                <div v-if="hasAttachments(note)" class="note-attachments">
                    <div class="attachments-title">
                        <i class="material-icons">attach_file</i>
                        {{ $t("message.attachments") }}
                    </div>
                    <div class="attachments-list">
                        <div
                            v-for="attachment in $_.get(note, '$attachments', [])"
                            :key="attachment.id"
                            class="attachment-item"
                        >
                            <i class="material-icons">insert_drive_file</i>
                            <span>{{ attachment.File_Name }}</span>
                            <span class="file-size">({{ formatFileSize(attachment.Size) }})</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Yeni not ekleme formu -->
        <div v-if="hasZohoChannel" class="add-note-form">
            <h3>{{ $t("message.addNewNote") }}</h3>
            <div class="form-group">
                <input v-model="newNote.title" type="text" :placeholder="$t('message.noteTitle')" class="note-input" />
            </div>
            <div class="form-group">
                <textarea v-model="newNote.content" :placeholder="$t('message.noteContent')" class="note-textarea"></textarea>
            </div>
            <button @click="saveNote" :disabled="saving" class="save-button">
                <i class="material-icons">save</i>
                {{ saving ? $t("message.savingg") : $t("message.save") }}
            </button>
        </div>
        <div v-else class="zoho-connect-message">
            {{ $t("message.connectToZoho") }}
            <a href="#" @click.prevent="$emit('connect-zoho')">{{ $t("message.connectZoho") }}</a>.
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import BucketSpinner from "@/views/Reports/common/spinners/BucketSpinner.vue";
import { ZOHO_API } from "../../../../api/zoho";
import { mapState } from "vuex";

export default Vue.extend({
    name: "DealNotes",
    components: {
        BucketSpinner,
    },
    props: {
        dealId: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            notes: [],
            loading: false,
            saving: false,
            newNote: {
                title: "",
                content: "",
            },
        };
    },
    async created() {
        await this.fetchNotes();
    },
    methods: {
        hasAttachments(note) {
            return this.$_.get(note, "$attachments.length", 0) > 0;
        },
        async fetchNotes() {
            this.loading = true;
            try {
                this.notes = await ZOHO_API.listDealNotes(this.dealId);
            } catch (error) {
            } finally {
                this.loading = false;
            }
        },
        formatDate(dateString) {
            if (!dateString) return "";
            return new Date(dateString).toLocaleDateString("tr-TR", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        formatFileSize(bytes) {
            const sizes = ["B", "KB", "MB", "GB"];
            if (bytes === 0) return "0 B";
            const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
            return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
        },
        async saveNote() {
            if (!this.newNote.title || !this.newNote.content) {
                this.$message.warning("Lütfen başlık ve içerik alanlarını doldurun.");
                return;
            }

            this.saving = true;
            try {
                const supsisUserId = this.$store.state.auth.user._id;
                await ZOHO_API.createDealNote(this.dealId, this.newNote.title, this.newNote.content, supsisUserId);

                // Formu temizle
                this.newNote.title = "";
                this.newNote.content = "";

                // Notları yenile
                await this.fetchNotes();

                this.$message.success("Not başarıyla eklendi.");
            } catch (error) {
                this.$message.error("Not eklenirken bir hata oluştu.");
            } finally {
                this.saving = false;
            }
        },
    },
    computed: {
        ...mapState({
            hasZohoChannel: (state) =>
                !!state.channel.channels.find((ch) => ch.type === "zoho" && ch.owner === state.auth.user._id),
        }),
    },
});
</script>

<style scoped lang="scss">
.deal-notes {
    .notes-loading,
    .no-notes {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px;
        color: #94a3b8;
        gap: 12px;

        i {
            font-size: 32px;
        }
    }

    .notes-list {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .note-item {
            background: #f8fafc;
            border-radius: 8px;
            padding: 16px;
            border: 1px solid #e2e8f0;

            .note-header {
                margin-bottom: 12px;

                .note-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 500;
                    font-size: 16px;
                    color: #2d3748;
                    margin-bottom: 8px;
                }

                .note-meta {
                    display: flex;
                    gap: 16px;
                    color: #64748b;
                    font-size: 13px;

                    span {
                        display: flex;
                        align-items: center;
                        gap: 4px;

                        i {
                            font-size: 16px;
                        }
                    }
                }
            }

            .note-content {
                color: #4a5568;
                font-size: 14px;
                line-height: 1.5;
                margin-bottom: 12px;
            }

            .note-attachments {
                border-top: 1px solid #e2e8f0;
                padding-top: 12px;

                .attachments-title {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 13px;
                    color: #64748b;
                    margin-bottom: 8px;
                }

                .attachments-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;

                    .attachment-item {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        font-size: 13px;
                        color: #4a5568;

                        .file-size {
                            color: #94a3b8;
                            font-size: 12px;
                        }
                    }
                }
            }
        }
    }

    .add-note-form {
        margin-top: 20px;
        padding: 16px;
        background: #f8fafc;
        border-radius: 8px;
        border: 1px solid #e2e8f0;

        h3 {
            font-size: 16px;
            color: #2d3748;
            margin-bottom: 16px;
        }

        .form-group {
            margin-bottom: 12px;
        }

        .note-input {
            width: 100%;
            padding: 8px;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            font-size: 14px;
        }

        .note-textarea {
            width: 100%;
            padding: 8px;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            font-size: 14px;
            min-height: 100px;
            resize: vertical;
        }

        .save-button {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            background: #3182ce;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;

            &:hover {
                background: #2c5282;
            }

            &:disabled {
                background: #94a3b8;
                cursor: not-allowed;
            }

            i {
                font-size: 18px;
            }
        }
    }

    .notes-loading {
        padding: 16px;

        .loading-skeleton {
            display: flex;
            flex-direction: column;
            gap: 16px;

            .skeleton-item {
                background: #f8fafc;
                border-radius: 8px;
                padding: 16px;
                border: 1px solid #e2e8f0;

                .skeleton-header {
                    margin-bottom: 12px;

                    .skeleton-title {
                        height: 20px;
                        width: 60%;
                        background: #e2e8f0;
                        border-radius: 4px;
                        margin-bottom: 8px;
                        animation: pulse 1.5s infinite;
                    }

                    .skeleton-meta {
                        display: flex;
                        gap: 16px;

                        .skeleton-date,
                        .skeleton-owner {
                            height: 16px;
                            width: 100px;
                            background: #e2e8f0;
                            border-radius: 4px;
                            animation: pulse 1.5s infinite;
                        }
                    }
                }

                .skeleton-content {
                    height: 60px;
                    background: #e2e8f0;
                    border-radius: 4px;
                    animation: pulse 1.5s infinite;
                }
            }
        }
    }

    .zoho-connect-message {
        padding: 16px;
        background: #f8fafc;
        border-radius: 8px;
        border: 1px solid #e2e8f0;
        color: #2d3748;
        font-size: 14px;

        a {
            color: #2c5282;
            text-decoration: underline;
            cursor: pointer;
        }
    }
}

@keyframes pulse {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
    100% {
        opacity: 1;
    }
}
</style>
